import { Link, createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { Suspense, lazy, useSyncExternalStore } from "react";
import { DEMO_FIXTURE_CLIENT_SLUG, DEMO_FIXTURE_MONTH } from "./pages/shared/demoFixtures";
import { Shell } from "./pages/shell/Shell";
import { isValidProposalMonth } from "./calendarUiModel";
import { ManagerAccessBoundary } from "./pages/auth";
import { DevQaProvisionPage } from "./pages/dev/DevQaProvisionPage";
import { isAppProviderModeAligned } from "./appProviderMode";
import { useAppProviderMode } from "./appProviderModeContext";
import { INTERNAL_CLIENT_REVIEW_ROUTE } from "./internalRoutes";

const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const UserGuidePage = lazy(() => import("./pages/user-guide/UserGuidePage").then((module) => ({ default: module.UserGuidePage })));
const ClientHomePage = lazy(() => import("./pages/client-home/ClientHomePage").then((module) => ({ default: module.ClientHomePage })));
const LifecycleWorkspacePage = lazy(() => import("./pages/lifecycle/LifecycleWorkspacePage").then((module) => ({ default: module.LifecycleWorkspacePage })));
// Client Delivery and the client-facing review link intentionally share one deck surface.
const ClientReviewWorkspacePage = lazy(() => import("./pages/client-review/ClientReviewPages").then((module) => ({ default: module.ClientReviewWorkspacePage })));
const ClientManagementPage = lazy(() => import("./pages/client-management/ClientManagementPage").then((module) => ({ default: module.ClientManagementPage })));
const AdminClientManagementPage = lazy(() => import("./pages/client-management/ClientManagementPage").then((module) => ({ default: module.AdminClientManagementPage })));
const AdminPage = lazy(() => import("./pages/admin/AdminPage").then((module) => ({ default: module.AdminPage })));
const AdminTeamPage = lazy(() => import("./pages/admin/AdminTeamPage").then((module) => ({ default: module.AdminTeamPage })));
const ClientReviewLinkPage = lazy(() => import("./pages/client-review/ClientReviewPages").then((module) => ({ default: module.ClientReviewLinkPage })));
const DeliveryAssetViewerPage = lazy(() => import("./pages/client-review/ClientReviewPages").then((module) => ({ default: module.DeliveryAssetViewerPage })));
const ClientDefaultsPage = lazy(() => import("./pages/configuration/ConfigurationPages").then((module) => ({ default: module.ClientDefaultsPage })));
const GlobalCalendarPage = lazy(() => import("./pages/configuration/ConfigurationPages").then((module) => ({ default: module.GlobalCalendarPage })));

function RootRoutePage() {
  const providerMode = useAppProviderMode();
  const routerPathname = useSyncExternalStore(
    subscribeToRouterState,
    getRouterStatePathname,
    getRouterStatePathname,
  );
  const historyPathname = useSyncExternalStore(
    subscribeToRouterHistory,
    getRouterHistoryPathname,
    getRouterHistoryPathname,
  );

  if (!isAppProviderModeAligned(providerMode, routerPathname, historyPathname)) {
    return <main className="page-grid"><div className="workspace-notice info" role="status">Preparing route…</div></main>;
  }

  return (
    <Suspense fallback={<main className="page-grid"><div className="workspace-notice info" role="status">Loading workspace…</div></main>}>
      <Shell />
    </Suspense>
  );
}

function getRouterStatePathname() {
  // The location updates before TanStack replaces the mounted matches. Use
  // the leaf route id so the auth-provider gate never treats an old internal
  // component tree as the new public route (or vice versa).
  const leafMatch = router.state.matches[router.state.matches.length - 1];
  return leafMatch?.routeId ?? router.state.location.pathname;
}

function getRouterHistoryPathname() {
  return router.history.location.pathname;
}

function subscribeToRouterHistory(listener: () => void) {
  return router.history.subscribe(() => listener());
}

function subscribeToRouterState(listener: () => void) {
  const unsubscribeHistory = router.history.subscribe(() => listener());
  const unsubscribeRendered = router.subscribe("onRendered", () => listener());
  return () => {
    unsubscribeHistory();
    unsubscribeRendered();
  };
}

function InvalidProposalMonth({ month }: { month: string }) {
  return (
    <section className="page-grid">
      <div className="workspace-notice warning" role="alert">
        <strong>That proposal month is not valid</strong>
        <span>{month} is not a real YYYY-MM month. Choose an available proposal from Clients.</span>
      </div>
      <Link to="/clients">Open Clients</Link>
    </section>
  );
}

function ClientHomeRoutePage() {
  const { clientSlug } = clientRoute.useParams();
  return <ClientHomePage clientSlug={clientSlug} />;
}

function CalendarRoutePage() {
  const { clientSlug, month } = calendarRoute.useParams();
  return <LifecycleWorkspacePage clientSlug={clientSlug} month={month} stage="ideation" />;
}

function BriefingRoutePage() {
  const { clientSlug, month } = briefingRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <LifecycleWorkspacePage clientSlug={clientSlug} month={month} stage="briefing" />;
}

function DesignRoutePage() {
  const { clientSlug, month } = designRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <LifecycleWorkspacePage clientSlug={clientSlug} month={month} stage="design" />;
}

function ClientReviewWorkspaceRoutePage() {
  const { clientSlug, month } = clientReviewWorkspaceRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <ClientReviewWorkspacePage clientSlug={clientSlug} month={month} />;
}

function ClientReviewLinkRoutePage() {
  const { reviewToken } = clientReviewRoute.useParams();
  return <ClientReviewLinkPage reviewToken={reviewToken} />;
}

function DeliveryAssetViewerRoutePage() {
  const { assetToken } = deliveryAssetViewerRoute.useParams();
  return <DeliveryAssetViewerPage assetToken={assetToken} />;
}

function ClientDefaultsRoutePage() {
  const { clientSlug } = clientDefaultsRoute.useParams();
  return (
    <ManagerAccessBoundary>
      <ClientDefaultsPage clientSlug={clientSlug} />
    </ManagerAccessBoundary>
  );
}

function MonthlyProposalConfigurationRoutePage() {
  const { clientSlug, month } = monthlyProposalConfigurationRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <LifecycleWorkspacePage clientSlug={clientSlug} month={month} stage="configuration" />;
}

const rootRoute = createRootRoute({ component: RootRoutePage });
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage,
});
const userGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/guide",
  component: UserGuidePage,
});
const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug",
  component: ClientHomeRoutePage,
});
const companyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/company",
  component: () => <ClientHomePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} />,
});
const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/calendar",
  component: CalendarRoutePage,
});
const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspace",
  component: () => <LifecycleWorkspacePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} stage="ideation" />,
});
const briefingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/briefing",
  component: BriefingRoutePage,
});
const briefingAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/briefing",
  component: () => <LifecycleWorkspacePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} stage="briefing" />,
});
const designRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/design",
  component: DesignRoutePage,
});
const designAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/design",
  component: () => <LifecycleWorkspacePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} stage="design" />,
});
const clientReviewWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: INTERNAL_CLIENT_REVIEW_ROUTE,
  component: ClientReviewWorkspaceRoutePage,
});
const clientReviewWorkspaceAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client-review",
  component: () => <ClientReviewWorkspacePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} />,
});
const clientReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client-review/$reviewToken",
  component: ClientReviewLinkRoutePage,
});
const deliveryAssetViewerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/delivery-asset/$assetToken",
  component: DeliveryAssetViewerRoutePage,
});
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients",
  component: ClientManagementPage,
});
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});
const adminTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/team",
  component: AdminTeamPage,
});
const adminClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/clients",
  component: () => (
    <ManagerAccessBoundary>
      <AdminClientManagementPage />
    </ManagerAccessBoundary>
  ),
});
const calendarsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/calendars",
  component: () => (
    <ManagerAccessBoundary>
      <GlobalCalendarPage />
    </ManagerAccessBoundary>
  ),
});
const clientDefaultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/defaults",
  component: ClientDefaultsRoutePage,
});
const monthlyProposalConfigurationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/proposal-configuration",
  component: MonthlyProposalConfigurationRoutePage,
});
const devQaProvisionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/__qa/provision",
  component: DevQaProvisionPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  userGuideRoute,
  clientRoute,
  companyRoute,
  calendarRoute,
  workspaceRoute,
  briefingRoute,
  briefingAliasRoute,
  designRoute,
  designAliasRoute,
  clientReviewWorkspaceRoute,
  clientReviewWorkspaceAliasRoute,
  clientReviewRoute,
  deliveryAssetViewerRoute,
  clientsRoute,
  adminRoute,
  adminTeamRoute,
  adminClientsRoute,
  calendarsRoute,
  clientDefaultsRoute,
  monthlyProposalConfigurationRoute,
  devQaProvisionRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
